import type { PostHog, PostHogConfig } from 'posthog-js'
import type { GetUser } from '@messenger/types'

import { EventCategory, logger } from '@/utils/logger'
import {
  scrubCaptureResult,
  scrubTrackedUrl,
} from '@/utils/global/scrubTrackedUrl'

// PostHog runtime state to prevent duplicate operations
const posthogRuntimeState = {
  interactionListenerRegistered: false,
  sessionRecordingInitialized: false,
  recordingAttempts: 0,
  interactionHandler: null as (() => void) | null,
}
const MAX_RECORDING_ATTEMPTS = 3

// Guard to check if we're in a browser environment
export const isBrowserEnvironment = (): boolean => {
  return typeof window !== 'undefined' && typeof navigator !== 'undefined'
}

/**
 * Comprehensive PostHog state data collector for consistent logging
 */
export const getPostHogStateData = (
  posthog: PostHog | null | undefined,
  options: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: any
    status?: string
    user?: GetUser
    funeralHomeId?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userProperties?: Record<string, any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additionalData?: Record<string, any>
  } = {},
) => {
  const {
    error = null,
    status = null,
    user = null,
    funeralHomeId = null,
    userProperties = {},
    additionalData = {},
  } = options

  return {
    // Error and status info
    error: error,
    status: status,

    // User context
    user: user?.id,
    userProperties: userProperties,
    funeralHomeId: funeralHomeId,

    // PostHog client state
    posthogAvailable: !!posthog,
    posthogLoaded: posthog?.has_opted_out_capturing?.() === false,
    posthogOptedOut: posthog?.has_opted_out_capturing?.(),
    posthogSessionId: posthog?.get_session_id?.(),
    posthogDistinctId: posthog?.get_distinct_id?.(),

    // Session recording state
    sessionId: posthog?.get_session_id?.() || 'N/A',
    sessionReplayUrl: posthog?.get_session_replay_url?.() || 'N/A',
    sessionRecordingStatus: posthog?.sessionRecording?.status,
    sessionRecordingEnabled: posthog?.sessionRecording !== undefined,
    sessionRecordingInitialized:
      posthogRuntimeState.sessionRecordingInitialized,

    // Configuration state
    posthogConfig: {
      session_recording_enabled: posthog?.sessionRecording?.status === 'active',
      capture_pageleave: true,
      persistence: 'localStorage',
    },
    featureFlagsSupported: typeof posthog?.getFeatureFlag === 'function',

    // Runtime state
    attempt_number: posthogRuntimeState.recordingAttempts,
    interactionListenerRegistered:
      posthogRuntimeState.interactionListenerRegistered,

    // Environment/Browser state
    url: isBrowserEnvironment() ? scrubTrackedUrl(window.location.href) : 'SSR',
    userAgent: isBrowserEnvironment() ? navigator.userAgent : 'SSR',
    browserSupportsRecording:
      isBrowserEnvironment() && 'MutationObserver' in window,
    localStorageAvailable: (() => {
      try {
        return isBrowserEnvironment() && typeof Storage !== 'undefined'
      } catch {
        return false
      }
    })(),

    // Timing
    timestamp: new Date().toISOString(),

    // Additional data passed in
    ...additionalData,
  }
}

/**
 * Standardized PostHog error logger
 */
export const logPostHogError = (
  message: string,
  posthog: PostHog | null | undefined,
  options: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: any
    status?: string
    user?: GetUser
    funeralHomeId?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userProperties?: Record<string, any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additionalData?: Record<string, any>
  } = {},
) => {
  logger.error({
    message,
    category: EventCategory.ERROR,
    error: options.error || new Error(message),
    data: getPostHogStateData(posthog, options),
  })
}

/**
 * Attach User Properties to PostHog analytics
 */
export const identifyUserInPosthog = (
  posthog: PostHog | null | undefined,
  user: GetUser,
  funeralHomeId?: string,
) => {
  if (!posthog) return

  const userProperties = {
    id: user.id,
    name: user.name,
    email: user.email,
    auth0_id: user.auth0Id,
    type: user.type,
    funeral_home_id: funeralHomeId,
    corporation_id: user.corporationId,
    ui_theme: user.uiTheme,
  }

  try {
    posthog.identify(user.id.toString(), userProperties)
    posthog.register(userProperties)
  } catch (error) {
    logPostHogError('posthog_identify_user_failed', posthog, {
      error: error,
      status: 'posthog failed to identify user in posthog.',
      user: user,
      userProperties: userProperties,
      funeralHomeId: funeralHomeId,
    })
  }
}

/**
 * Verify that session recording is properly enabled
 */
export const ensureSessionRecording = (
  posthog: PostHog | null | undefined,
): void => {
  if (!posthog || !isBrowserEnvironment()) return

  const shouldPreventExcessiveAttempts =
    posthogRuntimeState.sessionRecordingInitialized ||
    posthogRuntimeState.recordingAttempts >= MAX_RECORDING_ATTEMPTS

  if (shouldPreventExcessiveAttempts) {
    return
  }

  posthogRuntimeState.recordingAttempts++

  // Register session metadata to help with troubleshooting
  try {
    posthog.register({
      browser: navigator.userAgent,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      session_start_time: new Date().toISOString(),
    })
  } catch (error) {
    logPostHogError('register_session_metadata_failed', posthog, {
      error: error,
      status: 'posthog failed to register session metadata.',
    })
  }

  // Force start session recording using multiple approaches for better reliability
  try {
    // Method 1: Use the public API to explicitly start session recording
    if (typeof posthog.startSessionRecording === 'function') {
      posthog.startSessionRecording()
    }

    // Method 2: Also try to capture a specific event to ensure recording is active
    posthog.capture('$session_recording_ensure_active', {
      triggered_at: new Date().toISOString(),
      page_url: scrubTrackedUrl(window.location.href),
      user_agent: navigator.userAgent,
      attempt_number: posthogRuntimeState.recordingAttempts,
    })

    // Only mark as initialized if recording is actually active
    const isRecordingActive = checkSessionRecordingStatus(posthog)
    if (isRecordingActive) {
      posthogRuntimeState.sessionRecordingInitialized = true
    }
  } catch (e) {
    logPostHogError('posthog_ensure_session_recording_failed', posthog, {
      error: e,
    })
  }
}

/**
 * Capture page view with additional metadata
 */
export const capturePageView = (
  posthog: PostHog | null | undefined,
  pageName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties?: Record<string, any>,
): void => {
  if (!posthog || !isBrowserEnvironment()) return

  try {
    posthog.capture('$pageview', {
      page_name: pageName,
      url_path: window.location.pathname,
      referrer: scrubTrackedUrl(document.referrer),
      timestamp: new Date().toISOString(),
      ...properties,
    })
  } catch (error) {
    logPostHogError('capture_page_view_failed', posthog, {
      error: error,
      status: 'posthog failed to capture page view.',
    })
  }
}

/**
 * Check if session recording is actually active
 */
export const checkSessionRecordingStatus = (
  posthog: PostHog | null | undefined,
): boolean => {
  if (!posthog) return false

  try {
    // Try to access session recording status
    const sessionId = posthog.get_session_id?.()
    const sessionReplayUrl = posthog.get_session_replay_url?.()

    return !!(sessionId || sessionReplayUrl)
  } catch (e) {
    logPostHogError('check_session_recording_status_failed', posthog, {
      error: e,
      status: 'posthog failed to check session recording status.',
    })
    return false
  }
}

/**
 * Ensure session recording on user interaction (fallback method)
 */
export const ensureSessionRecordingOnInteraction = (
  posthog: PostHog | null | undefined,
): void => {
  if (!posthog || !isBrowserEnvironment()) return

  const shouldPreventDuplicateListeners =
    posthogRuntimeState.interactionListenerRegistered
  if (shouldPreventDuplicateListeners) return

  const isSessionRecordingActive = checkSessionRecordingStatus(posthog)
  if (isSessionRecordingActive) return

  posthogRuntimeState.interactionListenerRegistered = true
  let interactionHandled = false

  // Store the handler reference for proper cleanup
  posthogRuntimeState.interactionHandler = () => {
    if (interactionHandled) return
    interactionHandled = true

    try {
      ensureSessionRecording(posthog)

      // Check if recording is actually active after a short delay
      setTimeout(() => {
        const isActive = checkSessionRecordingStatus(posthog)

        if (!isActive) {
          console.warn(
            'PostHog session recording still not active after user interaction',
          )
        }
      }, 1000)
    } catch (e) {
      logPostHogError(
        'ensure_session_recording_on_interaction_failed',
        posthog,
        {
          error: e,
          status: 'posthog failed to ensure session recording on interaction.',
        },
      )
    }
  }

  const eventOptions = { once: true, passive: true }
  const eventTypes = ['click', 'keydown', 'scroll', 'mousemove', 'touchstart']

  // Add event listeners for user interactions
  eventTypes.forEach((eventType) => {
    document.addEventListener(
      eventType,
      posthogRuntimeState.interactionHandler!,
      eventOptions,
    )
  })
}

/**
 * Reset PostHog state (useful for testing or user logout)
 */
export const resetPostHogState = (): void => {
  posthogRuntimeState.interactionListenerRegistered = false
  posthogRuntimeState.sessionRecordingInitialized = false
  posthogRuntimeState.recordingAttempts = 0

  // Clean up event listeners to prevent memory leaks
  if (posthogRuntimeState.interactionHandler && isBrowserEnvironment()) {
    const eventTypes = ['click', 'keydown', 'scroll', 'mousemove', 'touchstart']

    eventTypes.forEach((eventType) => {
      document.removeEventListener(
        eventType,
        posthogRuntimeState.interactionHandler!,
      )
    })

    posthogRuntimeState.interactionHandler = null
  }
}

/**
 * Enhanced session recording with retry mechanism
 */
export const initializeSessionRecording = (
  posthog: PostHog | null | undefined,
): void => {
  if (!posthog || !isBrowserEnvironment()) return

  // Initial attempt
  ensureSessionRecording(posthog)

  // Set up interaction fallback
  ensureSessionRecordingOnInteraction(posthog)

  // Delayed retry if not active after initial attempt
  setTimeout(() => {
    const retryRecordingSession =
      !checkSessionRecordingStatus(posthog) &&
      posthogRuntimeState.recordingAttempts < MAX_RECORDING_ATTEMPTS

    if (retryRecordingSession) {
      console.log('Retrying PostHog session recording initialization...')
      ensureSessionRecording(posthog)
    }
  }, 2000)
}

/**
 * The options mdl-frontend starts PostHog with. Every event passes through
 * `scrubCaptureResult`, so no captured URL carries an edit link's `token` or
 * `access` value; session replay masks every input (passwords included) and
 * leaves the text on screen visible.
 */
export const buildPostHogOptions = (
  apiHost: string,
  debug: boolean,
): Partial<PostHogConfig> => ({
  api_host: apiHost,
  capture_pageleave: true,
  debug,
  before_send: scrubCaptureResult,
  on_request_error: (error) => {
    console.warn('[PostHog Request Error]:', error)
  },
  persistence: 'localStorage' as const,
  session_recording: {
    maskAllInputs: true,
    maskInputOptions: {
      password: true,
    },
  },
  loaded: (posthog) => {
    // This ensures session recording starts as early as possible
    try {
      initializeSessionRecording(posthog)
    } catch (error) {
      logPostHogError(
        'posthog_anonymous_recording_failed_in_provider',
        posthog,
        {
          error: error,
          status:
            'posthog session recording failed to initialize in an anonymous state from the provider.',
          additionalData: {
            api_host: apiHost,
          },
        },
      )
    }
  },
})
